/**
 * PROFILE SCREEN
 *
 * Driver profile with settings, vehicle info, certifications, and account management.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  Image, ActivityIndicator, Platform, Alert, RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSelector, useDispatch } from 'react-redux';
import { colors, spacing, RD } from '../../theme/colors';
import { api } from '../../services/api';
import { logout } from '../../store';
import { RootState } from '../../store';
import { MOCK_DRIVER } from '../../data/mockData';

const STATUS_CONFIG: Record<string, { color: string; label: string; icon: string }> = {
  ACTIVE:             { color: colors.success, label: 'Active',           icon: 'checkmark-circle' },
  PENDING_APPROVAL:   { color: colors.sunflower, label: 'Pending Review', icon: 'time' },
  ON_HOLD:            { color: colors.gray50, label: 'On Hold',           icon: 'pause-circle' },
  BANNED:             { color: colors.error, label: 'Suspended',          icon: 'close-circle' },
  TRAINING_REQUIRED:  { color: colors.skyBlue, label: 'Training Required', icon: 'school' },
  REGISTERED:         { color: colors.skyBlue, label: 'Registered',       icon: 'person-add' },
};

function formatVehicleType(type?: string): string {
  if (!type) return '—';
  return type
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c: string) => c.toUpperCase());
}

export function ProfileScreen({ navigation }: any) {
  const dispatch = useDispatch();
  const reduxDriver = useSelector((s: RootState) => s.auth.driver);
  const reduxCerts = useSelector((s: RootState) => s.auth.certifications);
  const [driver, setDriver] = useState<any>({
    ...MOCK_DRIVER,
    ...reduxDriver,
    name: reduxDriver?.name || '',
  });

  // Sync local driver state with Redux when it changes
  useEffect(() => {
    if (reduxDriver) {
      setDriver((prev: any) => ({ ...prev, ...reduxDriver, name: reduxDriver.name || prev.name }));
    }
  }, [reduxDriver]);

  const [refreshing, setRefreshing] = useState(false);
  const [reviews, setReviews] = useState<{ average: number | null; count: number; reviews: any[] }>({
    average: null,
    count: 0,
    reviews: [],
  });

  const loadProfile = useCallback(async () => {
    try {
      const r = await api.get('/drivers/me');
      if (r.data) setDriver(r.data);
    } catch {}
    try {
      const rv = await api.get('/drivers/me/reviews');
      if (rv.data) setReviews(rv.data);
    } catch {}
  }, []);

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', loadProfile);
    return unsubscribe;
  }, [navigation, loadProfile]);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadProfile();
    await new Promise(r => setTimeout(r, 600));
    setRefreshing(false);
  };

  const handleLogout = async () => {
    if (Platform.OS === 'web') {
      if (!window.confirm('Are you sure you want to log out?')) return;
      await AsyncStorage.removeItem('@cp_token');
      dispatch(logout());
    } else {
      Alert.alert('Log Out', 'Are you sure you want to log out?', [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Log Out',
          style: 'destructive',
          onPress: async () => {
            await AsyncStorage.removeItem('@cp_token');
            dispatch(logout());
          },
        },
      ]);
    }
  };

  const status = STATUS_CONFIG[driver.status] || STATUS_CONFIG.REGISTERED;
  const isActive = driver.status === 'ACTIVE';

  // Merge API certs with auto-approved Redux certs (logic preserved from prior layout)
  const apiCerts: any[] = driver.certifications || [];
  const allSlugs = new Set<string>(apiCerts.map((c: any) => c.categorySlug));
  const mergedCerts = [...apiCerts];
  for (const slug of reduxCerts) {
    if (!allSlugs.has(slug)) {
      mergedCerts.push({ id: `redux-${slug}`, categorySlug: slug, status: 'CERTIFIED' });
    }
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={RD.ink} />}
    >
      {/* Tap to refresh (web) */}
      {Platform.OS === 'web' && !refreshing && (
        <TouchableOpacity style={styles.refreshRow} onPress={onRefresh} activeOpacity={0.7}>
          <Ionicons name="refresh-outline" size={14} color={RD.textMuted} />
          <Text style={styles.refreshText}>Tap to refresh</Text>
        </TouchableOpacity>
      )}
      {refreshing && (
        <View style={styles.refreshRow}>
          <ActivityIndicator size="small" color={RD.ink} />
          <Text style={styles.refreshText}>Refreshing...</Text>
        </View>
      )}

      {/* Header Card — centered avatar + name + vehicle + active-status pill */}
      <View style={styles.profileCard}>
        {driver.profilePhotoUrl ? (
          <Image source={{ uri: driver.profilePhotoUrl }} style={styles.avatar} />
        ) : (
          <View style={styles.avatarPlaceholder}>
            <Text style={styles.avatarInitial}>
              {driver.name
                ? driver.name.split(' ').map((n: string) => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
                : 'D'}
            </Text>
          </View>
        )}
        <Text style={styles.profileName}>{driver.name}</Text>
        <View style={styles.vehicleLine}>
          <Ionicons name="car-outline" size={15} color={RD.textMuted} />
          <Text style={styles.vehicleText} numberOfLines={1}>
            {driver.vehicleDetails || formatVehicleType(driver.vehicleType)}
            {driver.vehicleDetails && driver.vehicleType ? ` · ${formatVehicleType(driver.vehicleType)}` : ''}
          </Text>
        </View>
        <View style={[styles.statusPill, { backgroundColor: isActive ? RD.moneySoft : status.color + '18' }]}>
          <Ionicons name={status.icon as any} size={13} color={isActive ? RD.money : status.color} />
          <Text style={[styles.statusPillText, { color: isActive ? RD.money : status.color }]}>
            {isActive ? 'Active driver' : status.label}
          </Text>
        </View>
      </View>

      {/* Contact rows card — email, phone, vehicle, radius */}
      <View style={styles.card}>
        <DataRow label="Email" value={driver.email} first />
        <DataRow label="Phone" value={driver.phone} />
        <DataRow label="Vehicle" value={driver.vehicleDetails} />
        <DataRow label="Type" value={formatVehicleType(driver.vehicleType)} />
        <DataRow label="Insurance" value={driver.insuranceProvider} />
        {/* The API column is `workingRadius`; `workingRadiusMiles` is the alias. Read BOTH —
            reading only the alias showed every driver a hardcoded "200 mi". */}
        <DataRow label="Working radius" value={`${driver.workingRadius ?? driver.workingRadiusMiles ?? 200} mi`} />
      </View>

      {/* Training shortcut row */}
      <TouchableOpacity style={styles.shortcutCard} activeOpacity={0.7} onPress={() => navigation.navigate('Training')}>
        <View style={[styles.shortcutIcon, { backgroundColor: RD.maroonSoft }]}>
          <Ionicons name="school-outline" size={19} color={RD.maroon} />
        </View>
        <View style={styles.shortcutInfo}>
          <Text style={styles.shortcutTitle}>Driver training</Text>
          <Text style={styles.shortcutSub}>Courses & certifications</Text>
        </View>
        <Ionicons name="chevron-forward" size={18} color={RD.g30} />
      </TouchableOpacity>

      {/* Certifications — maroon-soft pills */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Certifications</Text>
        {mergedCerts.length === 0 ? (
          <View style={styles.emptyCerts}>
            <Ionicons name="school-outline" size={24} color={RD.g30} />
            <Text style={styles.emptyCertsText}>No certifications yet</Text>
            <TouchableOpacity onPress={() => navigation.navigate('Training')}>
              <Text style={styles.emptyCertsLink}>Start Training</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.pillWrap}>
            {mergedCerts.map((c: any) => {
              const isAutoApproved = reduxCerts.includes(c.categorySlug);
              const displayStatus = isAutoApproved ? 'CERTIFIED' : c.status;
              const certified = displayStatus === 'CERTIFIED';
              return (
                <View
                  key={c.id || c.categorySlug}
                  style={[styles.certPill, { backgroundColor: certified ? RD.maroonSoft : RD.infoSoft }]}
                >
                  {certified && <Ionicons name="checkmark" size={12} color={RD.maroon} />}
                  <Text style={[styles.certPillText, { color: certified ? RD.maroon : RD.info }]}>
                    {c.categorySlug.replace(/-/g, ' ')}
                    {!certified ? ` · ${displayStatus.replace(/_/g, ' ').toLowerCase()}` : ''}
                  </Text>
                </View>
              );
            })}
          </View>
        )}
      </View>

      {/* Reviews card — real driver reviews from buyer/seller feedback */}
      <View style={styles.card}>
        <View style={styles.reviewsHeader}>
          <Text style={styles.cardTitle}>Reviews</Text>
          {reviews.count > 0 && reviews.average != null && (
            <View style={styles.ratingRow}>
              <Ionicons name="star" size={16} color={RD.amber} />
              <Text style={styles.ratingValue}>{reviews.average.toFixed(1)}</Text>
              <Text style={styles.ratingCount}>
                · {reviews.count} {reviews.count === 1 ? 'review' : 'reviews'}
              </Text>
            </View>
          )}
        </View>
        {reviews.count === 0 ? (
          <View style={styles.emptyReviews}>
            <Ionicons name="star-outline" size={24} color={RD.g30} />
            <Text style={styles.emptyReviewsText}>No reviews yet</Text>
          </View>
        ) : (
          reviews.reviews.map((rv: any) => {
            const score = Math.max(0, Math.min(5, Math.round(rv.score || 0)));
            return (
              <View key={rv.id} style={styles.reviewRow}>
                <View style={styles.reviewAvatar}>
                  <Ionicons name="person" size={16} color={RD.textMuted} />
                </View>
                <View style={styles.reviewBody}>
                  <View style={styles.reviewNameRow}>
                    <Text style={styles.reviewName}>{rv.reviewerLabel || 'Customer'}</Text>
                    <View style={styles.reviewStars}>
                      {[0, 1, 2, 3, 4].map((n) => (
                        <Ionicons
                          key={n}
                          name={n < score ? 'star' : 'star-outline'}
                          size={11}
                          color={RD.amber}
                        />
                      ))}
                    </View>
                  </View>
                  {rv.comment ? (
                    <Text style={styles.reviewText}>"{rv.comment}"</Text>
                  ) : rv.productTitle ? (
                    <Text style={styles.reviewText}>{rv.productTitle}</Text>
                  ) : null}
                </View>
              </View>
            );
          })
        )}
      </View>

      {/* Message support row */}
      <TouchableOpacity
        style={styles.shortcutCard}
        activeOpacity={0.7}
        onPress={() => navigation.navigate('SupportInbox')}
      >
        <View style={[styles.shortcutIcon, { backgroundColor: RD.infoSoft }]}>
          <Ionicons name="headset-outline" size={19} color={RD.info} />
        </View>
        <View style={styles.shortcutInfo}>
          <Text style={styles.shortcutTitle}>Message support</Text>
          <Text style={styles.shortcutSub}>Chat with the Commonplace team</Text>
        </View>
        <Ionicons name="chevron-forward" size={18} color={RD.g30} />
      </TouchableOpacity>

      {/* Edit profile row */}
      <TouchableOpacity
        style={styles.shortcutCard}
        activeOpacity={0.7}
        onPress={() => navigation.navigate('EditProfile', { driver })}
      >
        <View style={[styles.shortcutIcon, { backgroundColor: RD.g05 }]}>
          <Ionicons name="create-outline" size={19} color={RD.textSec} />
        </View>
        <View style={styles.shortcutInfo}>
          <Text style={styles.shortcutTitle}>Edit profile</Text>
          <Text style={styles.shortcutSub}>Details & working radius</Text>
        </View>
        <Ionicons name="chevron-forward" size={18} color={RD.g30} />
      </TouchableOpacity>

      {/* Logout — danger button */}
      <TouchableOpacity style={styles.logoutBtn} onPress={handleLogout} activeOpacity={0.7}>
        <Ionicons name="log-out-outline" size={20} color={RD.danger} />
        <Text style={styles.logoutText}>Log out</Text>
      </TouchableOpacity>

      <Text style={styles.version}>Commonplace Driver v1.0.0</Text>
    </ScrollView>
  );
}

function DataRow({ label, value, first }: { label: string; value?: string; first?: boolean }) {
  return (
    <View style={[styles.dataRow, first && { borderTopWidth: 0 }]}>
      <Text style={styles.dataLabel}>{label}</Text>
      <Text style={styles.dataValue} numberOfLines={1}>{value ?? '—'}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container:        { flex: 1, backgroundColor: RD.bg },
  content:          { padding: spacing.base, paddingTop: Platform.OS === 'ios' ? 60 : 40, paddingBottom: Platform.OS === 'ios' ? 110 : 88, gap: 12 },
  refreshRow:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 4 },
  refreshText:      { fontSize: 12, color: RD.textMuted, fontWeight: '500' },

  // Reusable warm-premium card (r18 + 1px border + subtle shadow)
  card:             { backgroundColor: RD.surface, borderRadius: 18, borderWidth: 1, borderColor: RD.border, padding: 16, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, shadowOffset: { width: 0, height: 1 }, elevation: 1 },
  cardTitle:        { fontSize: 14, fontWeight: '700', color: RD.text, marginBottom: 11 },

  // Profile header — centered
  profileCard:      { backgroundColor: RD.surface, borderRadius: 18, borderWidth: 1, borderColor: RD.border, padding: spacing.xl, alignItems: 'center', shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, shadowOffset: { width: 0, height: 1 }, elevation: 1 },
  avatar:           { width: 76, height: 76, borderRadius: 38, borderWidth: 1, borderColor: RD.border },
  avatarPlaceholder:{ width: 76, height: 76, borderRadius: 38, backgroundColor: RD.maroonSoft, justifyContent: 'center', alignItems: 'center' },
  avatarInitial:    { color: RD.maroon, fontSize: 28, fontWeight: '800', letterSpacing: 0.5 },
  profileName:      { fontSize: 19, fontWeight: '700', color: RD.text, marginTop: 12, letterSpacing: -0.3 },
  vehicleLine:      { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 4 },
  vehicleText:      { fontSize: 13, color: RD.textMuted, maxWidth: 240 },
  statusPill:       { flexDirection: 'row', alignItems: 'center', gap: 5, borderRadius: 999, paddingHorizontal: 12, paddingVertical: 5, marginTop: 11 },
  statusPillText:   { fontSize: 12, fontWeight: '700' },

  // Data rows
  dataRow:          { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 12, borderTopWidth: 1, borderTopColor: RD.g07 },
  dataLabel:        { fontSize: 13.5, color: RD.textMuted },
  dataValue:        { fontSize: 13.5, fontWeight: '600', color: RD.text, maxWidth: '58%', textAlign: 'right' },

  // Shortcut rows (training, support, edit)
  shortcutCard:     { backgroundColor: RD.surface, borderRadius: 18, borderWidth: 1, borderColor: RD.border, padding: 14, flexDirection: 'row', alignItems: 'center', gap: 13, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, shadowOffset: { width: 0, height: 1 }, elevation: 1 },
  shortcutIcon:     { width: 38, height: 38, borderRadius: 11, justifyContent: 'center', alignItems: 'center' },
  shortcutInfo:     { flex: 1 },
  shortcutTitle:    { fontSize: 14, fontWeight: '700', color: RD.text },
  shortcutSub:      { fontSize: 12, color: RD.textMuted, marginTop: 1 },

  // Certifications — pills
  pillWrap:         { flexDirection: 'row', flexWrap: 'wrap', gap: 7 },
  certPill:         { flexDirection: 'row', alignItems: 'center', gap: 4, borderRadius: 999, paddingHorizontal: 11, paddingVertical: 6 },
  certPillText:     { fontSize: 12, fontWeight: '700', textTransform: 'capitalize' },
  emptyCerts:       { alignItems: 'center', padding: spacing.base, gap: 6 },
  emptyCertsText:   { fontSize: 14, color: RD.textMuted },
  emptyCertsLink:   { fontSize: 14, color: RD.maroon, fontWeight: '700' },

  // Reviews
  reviewsHeader:    { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  ratingRow:        { flexDirection: 'row', alignItems: 'center', gap: 6 },
  ratingValue:      { fontSize: 14, fontWeight: '700', color: RD.text },
  ratingCount:      { fontSize: 12, color: RD.textFaint },
  reviewRow:        { flexDirection: 'row', gap: 11, paddingTop: 10, borderTopWidth: 1, borderTopColor: RD.g07 },
  reviewAvatar:     { width: 34, height: 34, borderRadius: 17, backgroundColor: RD.g07, justifyContent: 'center', alignItems: 'center' },
  reviewBody:       { flex: 1 },
  reviewNameRow:    { flexDirection: 'row', alignItems: 'center', gap: 6 },
  reviewName:       { fontSize: 13, fontWeight: '600', color: RD.text },
  reviewStars:      { flexDirection: 'row', gap: 1 },
  reviewText:       { fontSize: 12.5, color: RD.textSec, lineHeight: 18, marginTop: 3 },
  emptyReviews:     { alignItems: 'center', paddingVertical: spacing.base, gap: 6 },
  emptyReviewsText: { fontSize: 14, color: RD.textMuted },

  // Logout — danger
  logoutBtn:        { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 4, borderWidth: 1.5, borderColor: RD.danger + '40', borderRadius: 16, height: 54, backgroundColor: RD.dangerSoft },
  logoutText:       { color: RD.danger, fontWeight: '700', fontSize: 16 },

  version:          { textAlign: 'center', color: RD.textFaint, fontSize: 12, marginTop: 8 },
});
